/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Chip,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { theme } from "../../../Theme";
import {
  PROMO_ALL_BRANDS_ID,
  PROMO_ALL_BRANDS_OPTION,
  PROMO_ALL_CLASSES_ID,
  PROMO_DEFAULT_MANUFACTURER,
} from "../services/promoSimulatorService";
import { usePromoCatalog } from "../hooks/usePromoCatalog";
import {
  bandChipSx,
  formatBandDisplay,
  formatPromoTypeDisplay,
  getCellSelectedBand,
} from "../utils/promoDiscountBands";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function getMonthLabel(weekNum) {
  const idx = Math.min(Math.floor(((weekNum - 1) / 52) * 12), 11);
  return MONTH_LABELS[idx];
}

const TOTAL_WEEKS = 52;
const weeks = Array.from({ length: TOTAL_WEEKS }, (_, i) => i + 1);

export default function PlannerGrid({
  mode = "brand",
  events = [],
  gridData = null,
  modifiedCells = {},
  cellLoading = {},
  onCellChange,
  startWeek = 1,
  endWeek = 1,
  onStartWeekChange,
  onEndWeekChange,
  scenarioOptions = [],
  selectedScenarioId = "",
  onScenarioChange,
  scenarioDisabled = false,
  recommendedScenarioId = "",
  onRecommendedPlanApply,
  onPlannerReset,
}) {
  const brands = useMemo(() => [...new Set(events.map((e) => e.brand))], [events]);
  const [editing, setEditing] = useState(null); // { skuId, week, field }
  const [draftValue, setDraftValue] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState(PROMO_DEFAULT_MANUFACTURER);
  const [selectedBrandFilter, setSelectedBrandFilter] = useState(PROMO_ALL_BRANDS_ID);
  const [selectedProductClass, setSelectedProductClass] = useState(PROMO_ALL_CLASSES_ID);

  const {
    manufacturers,
    apiBrands,
    apiClasses,
    manufacturersLoading,
    brandsLoading,
    classesLoading,
  } = usePromoCatalog(selectedManufacturer, selectedBrandFilter, selectedProductClass);
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  /** SKU id → false when collapsed; omitted or true = expanded */
  const [expandedSku, setExpandedSku] = useState({});

  // Recommended-baseline gating
  const isRecommended =
    Boolean(recommendedScenarioId) &&
    String(selectedScenarioId) === String(recommendedScenarioId);
  const [draftStartWeek, setDraftStartWeek] = useState("");
  const [draftEndWeek, setDraftEndWeek] = useState("");

  const planWeeks = gridData?.weeks || weeks;
  const planApplied =
    mode === "sku" && String(gridData?.source ?? "") === "optimize_api";

  useEffect(() => {
    if (!isRecommended || planApplied) return;
    setDraftStartWeek((prev) => (prev ? prev : String(weeks[0])));
    setDraftEndWeek((prev) => (prev ? prev : String(weeks[weeks.length - 1])));
  }, [isRecommended, planApplied]);
  const rows = gridData?.rows || [];
  const safeStartWeek = Math.max(1, Math.min(52, Number(startWeek) || 1));
  const safeEndWeek = Math.max(
    safeStartWeek,
    Math.min(52, Number(endWeek) || safeStartWeek)
  );
  const selectedWeeks = useMemo(() => {
    if (mode !== "sku") return [];
    const list = planWeeks.filter((w) => w >= safeStartWeek && w <= safeEndWeek);
    return list.length ? list : [safeStartWeek];
  }, [mode, planWeeks, safeStartWeek, safeEndWeek]);

  const manufacturerOptions = useMemo(() => {
    return manufacturers;
  }, [manufacturers]);

  const brandChoices = useMemo(
    () => (brandsLoading ? [] : [PROMO_ALL_BRANDS_OPTION, ...apiBrands]),
    [apiBrands, brandsLoading]
  );

  const filteredRows = useMemo(() => {
    if (mode !== "sku") return [];
    const normEq = (a, b) =>
      String(a ?? "")
        .trim()
        .toLowerCase() ===
      String(b ?? "")
        .trim()
        .toLowerCase();
    return rows.filter((r) => {
      if (selectedManufacturer) {
        const rm = String(r?.manufacturer || "").trim();
        if (rm && !normEq(rm, selectedManufacturer)) return false;
      }
      if (selectedBrandFilter && selectedBrandFilter !== PROMO_ALL_BRANDS_ID) {
        const rowBrand = String(r?.brand || "").trim();
        if (!rowBrand) return false;
        const opt = apiBrands.find((b) => b.id === selectedBrandFilter);
        const candidates = [
          selectedBrandFilter,
          opt?.label,
          opt?.id,
        ]
          .filter(Boolean)
          .map((x) => String(x).trim());
        if (!candidates.some((c) => normEq(c, rowBrand))) return false;
      }
      if (selectedProductClass && selectedProductClass !== PROMO_ALL_CLASSES_ID) {
        const rowClass = String(r?.class || "").trim();
        if (rowClass && !normEq(rowClass, selectedProductClass)) return false;
      }
      return true;
    });
  }, [mode, rows, selectedManufacturer, selectedBrandFilter, selectedProductClass, apiBrands]);

  const firstCell =
    mode === "sku"
      ? rows?.[0]?.cells?.[safeStartWeek] ??
        rows?.[0]?.cells?.[planWeeks?.[0]] ??
        null
      : null;
  const useSimulatorKpis =
    mode === "sku" &&
    Boolean(firstCell?.kpis) &&
    ("expectedProfit" in firstCell.kpis ||
      "expectedVolumeL" in firstCell.kpis ||
      "upliftRatio" in firstCell.kpis ||
      "discountSpend" in firstCell.kpis);

  const formatCellValue = (field, value) => {
    if (value === undefined || value === null) return "—";
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "—";

    const decimalsByField = {
      expectedVolumeL: 0,
      upliftRatio: 2,
      expectedProfit: 0,
      discountSpend: 0,
      roi: 2,

      baseSales: 0,
      incrementalSales: 0,
      upliftPct: 1,
      marginImpact: 1,
      cannibalisation: 1,
      discountPct: 0,
    };

    const d =
      typeof decimalsByField[field] === "number" ? decimalsByField[field] : 0;
    return d > 0 ? n.toFixed(d) : String(Math.round(n));
  };

  useEffect(() => {
    if (mode !== "sku" || !gridData?.weeks?.length) return;
    if (isRecommended && !planApplied) return;
    const sorted = [...gridData.weeks].sort((a, b) => a - b);
    const minW = sorted[0];
    const maxW = sorted[sorted.length - 1];
    onStartWeekChange?.(minW);
    onEndWeekChange?.(maxW);
    setEditing(null);
    setDraftValue("");
    setExpandedSku({});
  }, [mode, gridData?.id, onStartWeekChange, onEndWeekChange, isRecommended, planApplied]);

  if (mode === "sku" && gridData) {
    const isSkuExpanded = (skuId) => expandedSku[skuId] !== false;
    const toggleSkuExpanded = (skuId) => {
      setExpandedSku((prev) => ({
        ...prev,
        [skuId]: prev[skuId] === false,
      }));
    };

    const renderSkuHeaderCell = (row, { rangeLabel, weekCount } = {}) => {
      const open = isSkuExpanded(row.skuId);
      return (
        <TableCell sx={{ fontWeight: 600, maxWidth: 300, verticalAlign: "middle" }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.25}>
            <IconButton
              size="small"
              aria-expanded={open}
              aria-label={
                open
                  ? `Collapse weeks for ${row.skuLabel || "SKU"}`
                  : `Expand weeks for ${row.skuLabel || "SKU"}`
              }
              onClick={() => toggleSkuExpanded(row.skuId)}
              sx={{ mt: -0.25, color: "text.secondary" }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 22,
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
            <Box sx={{ minWidth: 0, pt: 0.35 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                  fontSize: 13,
                }}
              >
                {row.skuLabel}
              </Typography>
              {rangeLabel ? (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                  {rangeLabel}
                  {!open && weekCount > 1 ? ` · ${weekCount} weeks hidden` : ""}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </TableCell>
      );
    };

    const renderSkuSeparator = (rowIndex, colSpan) =>
      rowIndex < filteredRows.length - 1 ? (
        <TableRow key={`sep_${rowIndex}`}>
          <TableCell
            colSpan={colSpan}
            sx={{
              p: 0,
              height: 12,
              border: "none",
              borderBottom: "2px solid #E2E8F0",
              backgroundColor: "#F4F6F9",
            }}
          />
        </TableRow>
      ) : null;

    const isEditing = (skuId, week, field) =>
      editing?.skuId === skuId &&
      Number(editing?.week) === Number(week) &&
      editing?.field === field;

    const commitDiscountEdit = async (skuId, week) => {
      setEditing(null);
      const pct = Number(draftValue);
      if (!onCellChange || !Number.isFinite(pct) || draftValue.trim() === "") return;
      const clamped = Math.max(0, Math.min(100, pct));
      await onCellChange({
        skuId,
        week,
        patch: { discountPct: clamped, userEnteredPct: clamped },
        field: "discountPct",
      });
    };

    const formatDiscountPctLabel = (pct) => {
      const n = Number(pct);
      if (!Number.isFinite(n)) return "—";
      return n % 1 === 0 ? `${n}%` : `${n.toFixed(1)}%`;
    };

    const getDiscountDisplayLabel = (cell, modified) => {
      const entered = cell?.userEnteredPct ?? cell?.discountPct;
      if (modified || cell?.userEnteredPct != null) {
        if (entered != null && Number.isFinite(Number(entered))) {
          return formatDiscountPctLabel(entered);
        }
      }
      const fromApi =
        cell?.selectedDiscount != null && Number.isFinite(Number(cell.selectedDiscount))
          ? Number(cell.selectedDiscount) * 100
          : cell?.kpis?.selectedDiscount != null &&
              Number.isFinite(Number(cell.kpis.selectedDiscount))
            ? Number(cell.kpis.selectedDiscount) * 100
            : null;
      if (fromApi != null) return formatDiscountPctLabel(fromApi);
      return formatBandDisplay(getCellSelectedBand(cell));
    };

    const renderDiscountCell = (row, cell, baseKey, weekNum) => {
      const loading = Boolean(cellLoading?.[baseKey]);
      if (loading) return <CircularProgress size={16} />;

      const band = getCellSelectedBand(cell);
      const modified = Boolean(modifiedCells?.[`${baseKey}_discountPct`]);
      const label = getDiscountDisplayLabel(cell, modified);

      if (isEditing(row.skuId, weekNum, "discountPct")) {
        return (
          <TextField
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onBlur={() => commitDiscountEdit(row.skuId, weekNum)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitDiscountEdit(row.skuId, weekNum);
              if (e.key === "Escape") setEditing(null);
            }}
            size="small"
            placeholder="%"
            inputProps={{ style: { fontSize: 12, textAlign: "center", width: 56 } }}
            sx={{ "& .MuiOutlinedInput-root": { height: 28 } }}
            autoFocus
          />
        );
      }

      return (
        <Tooltip title="Click to enter a discount %" arrow>
          <Chip
            label={label}
            size="small"
            onClick={() => {
              const currentPct = cell?.userEnteredPct ?? cell?.discountPct;
              setEditing({ skuId: row.skuId, week: weekNum, field: "discountPct" });
              setDraftValue(currentPct != null ? String(currentPct) : "");
            }}
            sx={{
              ...bandChipSx(band),
              cursor: "pointer",
              border: modified
                ? `1px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.primary.main}40`,
              fontWeight: modified ? 700 : 600,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}14`,
              },
            }}
          />
        </Tooltip>
      );
    };

    const renderEditable = (row, cell, baseKey, weekNum, field, value, suffix = "") => {
      const loading = Boolean(cellLoading?.[baseKey]);
      if (loading) return <CircularProgress size={16} />;

      return (
        <Box sx={{ fontSize: 12, fontWeight: 500 }}>
          {formatCellValue(field, value)}
          {value !== undefined && value !== null ? suffix : ""}
        </Box>
      );
    };

    const handlePlannerResetClick = async () => {
      if (!onPlannerReset) return;
      setResetLoading(true);
      try {
        setDraftStartWeek("");
        setDraftEndWeek("");
        setExpandedSku({});
        setSelectedManufacturer(PROMO_DEFAULT_MANUFACTURER);
        setSelectedBrandFilter(PROMO_ALL_BRANDS_ID);
        setEditing(null);
        setDraftValue("");
        await onPlannerReset();
      } finally {
        setResetLoading(false);
      }
    };

    const handleAddPlan = async () => {
      const sw = planApplied ? safeStartWeek : Number(draftStartWeek);
      const ew = planApplied ? safeEndWeek : Number(draftEndWeek);
      if (!sw || !ew || ew < sw) return;
      setOptimizeLoading(true);
      try {
        await (onRecommendedPlanApply &&
          onRecommendedPlanApply({
            startWeek: sw,
            endWeek: ew,
            manufacturer: selectedManufacturer,
            brand: selectedBrandFilter,
            productClass: selectedProductClass,
          }));
        onStartWeekChange && onStartWeekChange(sw);
        onEndWeekChange && onEndWeekChange(ew);
      } catch {
        // error surfaced by parent via plannerError
      } finally {
        setOptimizeLoading(false);
      }
    };
    const canRecommendPlan = true;

    return (
      <Box sx={{ position: "relative", width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid #F2F4F7",
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        {/* ── Filter bar ── always visible ── */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 1.75, sm: 2 },
            borderRadius: 2,
            border: "1px solid #F2F4F7",
            backgroundColor: "#FAFBFC",
          }}
        >
              <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    width: "100%",
                    alignItems: "flex-start",
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 260 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      },
                    }}
                  >
                    <InputLabel id="planner-scenario-label">Scenario</InputLabel>
                    <Select
                      labelId="planner-scenario-label"
                      value={selectedScenarioId}
                      label="Scenario"
                      onChange={(e) =>
                        onScenarioChange && onScenarioChange(e.target.value)
                      }
                      disabled={scenarioDisabled}
                    >
                      {scenarioOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 220 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      },
                    }}
                    disabled={manufacturersLoading || manufacturerOptions.length === 0}
                  >
                    <InputLabel id="planner-manufacturer-label">Manufacturer</InputLabel>
                    <Select
                      labelId="planner-manufacturer-label"
                      value={
                        manufacturerOptions.includes(selectedManufacturer)
                          ? selectedManufacturer
                          : manufacturerOptions[0] ?? PROMO_DEFAULT_MANUFACTURER
                      }
                      label="Manufacturer"
                      onChange={(e) => {
                        setSelectedManufacturer(e.target.value);
                        setSelectedBrandFilter(PROMO_ALL_BRANDS_ID);
                        setSelectedProductClass(PROMO_ALL_CLASSES_ID);
                      }}
                    >
                      {manufacturerOptions.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 200 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      },
                    }}
                    disabled={brandsLoading}
                  >
                    <InputLabel id="planner-brand-filter-label">Brand</InputLabel>
                    <Select
                      labelId="planner-brand-filter-label"
                      value={
                        selectedBrandFilter &&
                        brandChoices.some((b) => b.id === selectedBrandFilter)
                          ? selectedBrandFilter
                          : PROMO_ALL_BRANDS_ID
                      }
                      label="Brand"
                      displayEmpty
                      onChange={(e) => {
                        setSelectedBrandFilter(e.target.value);
                        setSelectedProductClass(PROMO_ALL_CLASSES_ID);
                      }}
                    >
                      {brandChoices.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 200 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      },
                    }}
                    disabled={classesLoading}
                  >
                    <InputLabel id="planner-class-filter-label">Class</InputLabel>
                    <Select
                      labelId="planner-class-filter-label"
                      value={
                        selectedProductClass &&
                        apiClasses.some((c) => c.id === selectedProductClass)
                          ? selectedProductClass
                          : ""
                      }
                      label="Class"
                      displayEmpty
                      onChange={(e) =>
                        setSelectedProductClass(e.target.value || null)
                      }
                    >
                      {apiClasses.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Start week */}
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 180 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      },
                    }}
                  >
                    <InputLabel id="planner-week-start-label">Start week</InputLabel>
                    {isRecommended && !planApplied ? (
                      <Select
                        labelId="planner-week-start-label"
                        value={draftStartWeek}
                        label="Start week"
                        displayEmpty
                        onChange={(e) => {
                          setDraftStartWeek(e.target.value);
                          if (draftEndWeek && Number(e.target.value) > Number(draftEndWeek)) {
                            setDraftEndWeek(e.target.value);
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em></em>
                        </MenuItem>
                        {weeks.map((w) => (
                          <MenuItem key={w} value={w}>
                            {`W${String(w).padStart(2, "0")}`}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Select
                        labelId="planner-week-start-label"
                        value={safeStartWeek}
                        label="Start week"
                        onChange={(e) =>
                          onStartWeekChange && onStartWeekChange(Number(e.target.value))
                        }
                      >
                        {weeks.map((w) => (
                          <MenuItem key={w} value={w}>
                            {`W${String(w).padStart(2, "0")}`}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>

                  {/* End week */}
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 180 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                      },
                    }}
                  >
                    <InputLabel id="planner-week-end-label">End week</InputLabel>
                    {isRecommended && !planApplied ? (
                      <Select
                        labelId="planner-week-end-label"
                        value={draftEndWeek}
                        label="End week"
                        displayEmpty
                        onChange={(e) => setDraftEndWeek(e.target.value)}
                      >
                        <MenuItem value="" disabled>
                          <em></em>
                        </MenuItem>
                        {weeks
                          .filter((w) => !draftStartWeek || w >= Number(draftStartWeek))
                          .map((w) => (
                            <MenuItem key={w} value={w}>
                              {`W${String(w).padStart(2, "0")}`}
                            </MenuItem>
                          ))}
                      </Select>
                    ) : (
                      <Select
                        labelId="planner-week-end-label"
                        value={safeEndWeek}
                        label="End week"
                        onChange={(e) =>
                          onEndWeekChange && onEndWeekChange(Number(e.target.value))
                        }
                      >
                        {weeks.map((w) => (
                          <MenuItem key={w} value={w}>
                            {`W${String(w).padStart(2, "0")}`}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                </Box>

                {isRecommended && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    justifyContent={{ xs: "stretch", sm: "flex-end" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      pt: 0.25,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!canRecommendPlan || optimizeLoading || resetLoading}
                      onClick={handleAddPlan}
                      sx={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 148 },
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        px: 1.75,
                        py: 0.9,
                        gap: 1,
                      }}
                    >
                      {optimizeLoading && (
                        <CircularProgress size={14} color="inherit" />
                      )}
                      {"Recommend Plan"}
                    </Button>
                    {onPlannerReset && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={optimizeLoading || resetLoading}
                        onClick={handlePlannerResetClick}
                        startIcon={
                          resetLoading ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <RestartAltIcon sx={{ fontSize: 18 }} />
                          )
                        }
                        sx={{
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          width: { xs: "100%", sm: "auto" },
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 13,
                          px: 1.75,
                          py: 0.9,
                        }}
                      >
                        Reset planner
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
        </Paper>

        {/* ── Content area ── */}
        {isRecommended && !planApplied ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            gap={1}
          >
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              Select your filters above and click <strong>Recommend Plan</strong> to
              generate an optimized promotional plan.
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Choose manufacturer, brand (or All brands), class, start week, and end week
              to get started.
            </Typography>
          </Box>
        ) : filteredRows.length === 0 ? (
          <Typography color="text.secondary" variant="body2" py={3} textAlign="center">
            No SKUs available in this scenario.
          </Typography>
        ) : (
          <>
            {/* KPI table — one row per week when range spans multiple weeks */}
            <TableContainer
              sx={{
                border: "1px solid #F2F4F7",
                borderRadius: 2,
                maxHeight: { xs: "65vh", lg: "80vh"},
                overflow: "auto",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {(useSimulatorKpis
                      ? [
                          "SKU",
                          ...(selectedWeeks.length > 1 ? ["Week"] : []),
                          "Discount",
                          "Promo type",
                          "Expected volume (L)",
                          "Uplift ratio",
                          "Expected profit",
                          "Discount spend",
                          "ROI",
                        ]
                      : [
                          "SKU",
                          ...(selectedWeeks.length > 1 ? ["Week"] : []),
                          "Discount",
                          "Base Sales",
                          "Incremental Sales",
                          "Uplift %",
                          "ROI",
                          "Margin Impact",
                          "Cannibalisation",
                        ]
                    ).map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          fontSize: 12,
                          backgroundColor: "#FAFBFC",
                          borderBottom: "1px solid #E8ECF0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row, rowIndex) => {
                    const showWeekBreakdown = selectedWeeks.length > 1;
                    const weekRows = showWeekBreakdown ? selectedWeeks : [selectedWeeks[0] ?? safeStartWeek];
                    const tableColCount = useSimulatorKpis
                      ? showWeekBreakdown
                        ? 9
                        : 8
                      : showWeekBreakdown
                        ? 9
                        : 8;

                    const renderKpiCells = (weekNum, cell) => {
                      const baseKey = `${row.skuId}_${weekNum}`;

                      return (
                        <>
                          <TableCell sx={{ minWidth: 110 }}>
                            {renderDiscountCell(row, cell, baseKey, weekNum)}
                          </TableCell>
                          {useSimulatorKpis ? (
                            <>
                              <TableCell
                                sx={{
                                  minWidth: 88,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "text.secondary",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatPromoTypeDisplay(
                                  cell?.promoType ?? cell?.kpis?.promoType
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "expectedVolumeL",
                                  cell?.kpis?.expectedVolumeL
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "upliftRatio",
                                  cell?.kpis?.upliftRatio,
                                  "×"
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "expectedProfit",
                                  cell?.kpis?.expectedProfit
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "discountSpend",
                                  cell?.kpis?.discountSpend
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "roi",
                                  cell?.kpis?.roi,
                                  "x"
                                )}
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "baseSales",
                                  cell?.kpis?.baseSales
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "incrementalSales",
                                  cell?.kpis?.incrementalSales
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "upliftPct",
                                  cell?.kpis?.upliftPct,
                                  "%"
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "roi",
                                  cell?.kpis?.roi,
                                  "x"
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "marginImpact",
                                  cell?.kpis?.marginImpact
                                )}
                              </TableCell>
                              <TableCell>
                                {renderEditable(
                                  row,
                                  cell,
                                  baseKey,
                                  weekNum,
                                  "cannibalisation",
                                  cell?.kpis?.cannibalisation,
                                  "%"
                                )}
                              </TableCell>
                            </>
                          )}
                        </>
                      );
                    };

                    const skuOpen = isSkuExpanded(row.skuId);
                    const rangeLabel =
                      selectedWeeks.length > 1
                        ? `W${String(selectedWeeks[0]).padStart(2, "0")}–W${String(
                            selectedWeeks[selectedWeeks.length - 1]
                          ).padStart(2, "0")}`
                        : null;

                    if (!showWeekBreakdown) {
                      const weekNum = weekRows[0];
                      const cell = row.cells?.[weekNum];
                      return (
                        <React.Fragment key={row.skuId}>
                          <TableRow
                            hover
                            sx={{
                              backgroundColor: "#FAFBFC",
                              "& td": { borderColor: "#EEF1F5" },
                            }}
                          >
                            {renderSkuHeaderCell(row, { rangeLabel: null, weekCount: 1 })}
                            {skuOpen ? (
                              renderKpiCells(weekNum, cell)
                            ) : (
                              <TableCell
                                colSpan={tableColCount - 1}
                                sx={{ color: "text.disabled", fontSize: 12 }}
                              >
                                Collapsed
                              </TableCell>
                            )}
                          </TableRow>
                          {renderSkuSeparator(rowIndex, tableColCount)}
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={row.skuId}>
                        <TableRow
                          sx={{
                            backgroundColor: "#FAFBFC",
                            "& td": { borderColor: "#EEF1F5" },
                          }}
                        >
                          {renderSkuHeaderCell(row, {
                            rangeLabel,
                            weekCount: weekRows.length,
                          })}
                          <TableCell
                            colSpan={tableColCount - 1}
                            sx={{ color: "text.disabled", fontSize: 12 }}
                          >
                            {skuOpen
                              ? `${weekRows.length} week${weekRows.length === 1 ? "" : "s"}`
                              : "Collapsed"}
                          </TableCell>
                        </TableRow>
                        {skuOpen &&
                          weekRows.map((weekNum, idx) => {
                            const cell = row.cells?.[weekNum];
                            return (
                              <TableRow
                                key={`${row.skuId}_w${weekNum}`}
                                hover
                                sx={{
                                  backgroundColor: idx % 2 === 0 ? "#fff" : "#F8FAFC",
                                  "& td": { borderColor: "#EEF1F5" },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    pl: 5,
                                    borderRight: "1px solid #EEF1F5",
                                    backgroundColor: "inherit",
                                  }}
                                />
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: 12,
                                    color: "text.secondary",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {`W${String(weekNum).padStart(2, "0")}`}
                                </TableCell>
                                {renderKpiCells(weekNum, cell)}
                              </TableRow>
                            );
                          })}
                        {renderSkuSeparator(rowIndex, tableColCount)}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
      {optimizeLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
            bgcolor: "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(2px)",
            borderRadius: 2,
            border: "1px solid #F2F4F7",
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Loading optimized plan…
          </Typography>
        </Box>
      )}
    </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid #F2F4F7",
        borderRadius: 2,
        overflowX: "auto",
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        color={theme.palette.primary.main}
        mb={2}
      >
        Promo Calendar 2024
      </Typography>

      <Box sx={{ minWidth: 900 }}>
        {/* Month row */}
        <Box display="flex" mb={0.5}>
          <Box sx={{ minWidth: 110, flexShrink: 0 }} />
          {weeks.map((w) => (
            <Box
              key={w}
              sx={{
                minWidth: 26,
                textAlign: "center",
                fontSize: 9,
                color: "text.secondary",
                fontWeight: w % 4 === 1 ? 700 : 400,
              }}
            >
              {w % 4 === 1 ? getMonthLabel(w) : ""}
            </Box>
          ))}
        </Box>

        {/* Week numbers row */}
        <Box display="flex" mb={1.5}>
          <Box
            sx={{
              minWidth: 110,
              flexShrink: 0,
              fontSize: 9,
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
            }}
          >
            Week →
          </Box>
          {weeks.map((w) => (
            <Box
              key={w}
              sx={{
                minWidth: 26,
                textAlign: "center",
                fontSize: 8,
                color: "#bbb",
                borderLeft: w % 4 === 1 ? "1px dashed #e0e0e0" : "none",
              }}
            >
              {w % 4 === 1 ? w : ""}
            </Box>
          ))}
        </Box>

        {/* Brand rows */}
        {brands.length === 0 ? (
          <Typography color="text.secondary" variant="body2" py={3} textAlign="center">
            No promotional events planned yet.
          </Typography>
        ) : (
          brands.map((brand) => {
            const brandEvents = events.filter((e) => e.brand === brand);
            return (
              <Box key={brand} display="flex" alignItems="center" mb={0.75}>
                <Box
                  sx={{
                    minWidth: 110,
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    pr: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {brand}
                </Box>
                {weeks.map((w) => {
                  const ev = brandEvents.find(
                    (e) => w >= e.weekStart && w <= e.weekEnd
                  );
                  const isStart = ev && w === ev.weekStart;
                  const isEnd = ev && w === ev.weekEnd;
                  return (
                    <Tooltip
                      key={w}
                      title={
                        ev
                          ? `${ev.scenarioName} — ${ev.discountPct}% off`
                          : ""
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          minWidth: 26,
                          height: 22,
                          backgroundColor: ev
                            ? ev.color || theme.palette.primary.main
                            : "#F2F4F7",
                          opacity: ev ? 0.85 : 1,
                          borderRadius: isStart && isEnd
                            ? "4px"
                            : isStart
                            ? "4px 0 0 4px"
                            : isEnd
                            ? "0 4px 4px 0"
                            : 0,
                          cursor: ev ? "pointer" : "default",
                          transition: "opacity 0.15s",
                          "&:hover": ev ? { opacity: 1 } : {},
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })
        )}

        {/* Legend */}
        {events.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap" mt={2.5}>
            {events.map((e) => (
              <Chip
                key={e.id}
                label={`${e.scenarioName} (${e.discountPct}% off)`}
                size="small"
                sx={{
                  backgroundColor: e.color || theme.palette.primary.main,
                  color: "#fff",
                  fontSize: 10,
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}
